import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Category {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
}

export function useCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCategories = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("sort_order", { ascending: true });

    if (data) {
      setCategories(data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const addCategory = async (name: string) => {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");
    
    const maxOrder = categories.length > 0 
      ? Math.max(...categories.map(c => c.sort_order)) 
      : 0;

    const { data, error } = await supabase
      .from("categories")
      .insert({ name, slug, sort_order: maxOrder + 1 })
      .select()
      .single();

    if (error) {
      return { error };
    }

    await fetchCategories();
    return { data };
  };

  const updateCategory = async (id: string, name: string) => {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");

    const { error } = await supabase
      .from("categories")
      .update({ name, slug })
      .eq("id", id);

    if (error) {
      return { error };
    }

    await fetchCategories();
    return { success: true };
  };

  const deleteCategory = async (id: string) => {
    const { error } = await supabase
      .from("categories")
      .delete()
      .eq("id", id);

    if (error) {
      return { error };
    }

    await fetchCategories();
    return { success: true };
  };

  const reorderCategories = async (reorderedCategories: Category[]) => {
    const updates = reorderedCategories.map((cat, index) => ({
      id: cat.id,
      name: cat.name,
      slug: cat.slug,
      sort_order: index + 1,
    }));

    const { error } = await supabase
      .from("categories")
      .upsert(updates);

    if (error) {
      return { error };
    }

    setCategories(reorderedCategories.map((cat, index) => ({ ...cat, sort_order: index + 1 })));
    return { success: true };
  };

  return {
    categories,
    isLoading,
    addCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    refetch: fetchCategories,
  };
}
